import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import {
  createFormErrorMessage,
  pushFormattedError
} from 'helpers/formValidationHelper/formValidationMessage';
import {
  createGroup,
  getAvailableProducts,
  getGroupById,
  getGroups,
  getProducts,
  searchGroups,
  updateGroup,
  updateGroupTranslation
} from 'api/groups';
import {
  getCurrentPage,
  getLimit,
  getSearch,
  getTranslationRequest,
  getUpdateGroupRequest,
  getUserGroup,
  getUserGroups
} from './groupsSelectors';
import { getAuth } from 'actions/auth/login';
import { getCountriesDropdown } from 'api/users';
import { getGroupLanguageCode } from './groupsSelectors';
import { getLocale } from 'actions/language/selectors';
import languageFilterOptions from 'conf/platformLanguages';
import { pushAttentionNotification } from 'actions/notification/notification';
import { translationFields } from 'conf/userGroupFields';

/*** Actions ***/
export const ADD_GROUP = 'ACCOUNTS/GROUPS/ADD_GROUP';
export const CHANGE_PAGE = 'UI/ACCOUNTS/GROUPS/CHANGE_PAGE';
export const HANDLE_NEW_GROUP_EDIT = 'ACCOUNTS/GROUPS/HANDLE_NEW_GROUP_EDIT';
export const HANDLE_NEW_GROUP_PARENT_EDIT =
  'ACCOUNTS/GROUPS/HANDLE_NEW_GROUP_PARENT_EDIT';
export const SEARCH_USER_GROUPS = 'ACCOUNTS/GROUPS/SEARCH_USER_GROUPS';
export const SEND_UPDATE = 'ACCOUNTS/GROUPS/SEND_UPDATE';
export const SET_GROUP_LANGUAGE = 'ACCOUNTS/GROUPS/SET_GROUP_LANGUAGE';
export const SET_GROUPS = 'ACCOUNTS/GROUPS/SET_GROUPS';
export const SET_LIMIT = 'UI/ACCOUNTS/GROUPS/SET_LIMIT';
export const SET_PARENT_GROUP_SUGGESTIONS =
  'ACCOUNTS/GROUPS/SET_PARENT_GROUP_SUGGESTIONS';
export const SET_SEARCH_TEXT = 'UI/ACCOUNTS/GROUPS/SET_SEARCH_TEXT';
export const TOGGLE_ADD_GROUP_MODAL =
  'UI/ACCOUNTS/GROUPS/TOGGLE_ADD_GROUP_MODAL';
export const TOGGLE_EDIT_MODAL = 'UI/ACCOUNTS/GROUPS/TOGGLE_EDIT_MODAL';
export const TOGGLE_GROUP_LANGUAGE_DROPDOWN =
  'UI/ACCOUNTS/GROUPS/TOGGLE_GROUP_LANGUAGE_DROPDOWN';
export const TOGGLE_LIMIT_DROPDOWN = 'UI/ACCOUNTS/GROUPS/TOGGLE_LIMIT_DROPDOWN';
export const TOGGLE_SEARCH_DROPDOWN =
  'UI/ACCOUNTS/GROUPS/TOGGLE_SEARCH_DROPDOWN';
export const UPDATE_GROUP_SUCCESS = 'ACCOUNTS/GROUPS/UPDATE_GROUP_SUCCESS';
export const UPDATE_GROUP_LANG_FIELDS =
  'ACCOUNTS/GROUPS/UPDATE_GROUP_LANG_FIELDS';
export const FETCH_PRODUCTS = 'ACCOUNTS/GROUPS/FETCH_PRODUCTS';
export const UPDATE_PRODUCTS = 'ACCOUNTS/GROUPS/UPDATE_PRODUCTS';
export const GET_ALL_PRODUCTS_SUCCESS =
  'ACCOUNTS/GROUPS/GET_ALL_PRODUCTS_SUCCESS';
export const GET_AVAILABLE_PRODUCTS = 'ACCOUNTS/GROUPS/GET_AVAILABLE_PRODUCTS';

/*** Action Creators ***/
const fetchGroupsSuccess = (data, countries, languageCode) => ({
  type: SET_GROUPS,
  ...data,
  countries,
  languageCode
});

const updateGroupSuccess = (group, index) => ({
  type: UPDATE_GROUP_SUCCESS,
  group,
  index
});

const updateGroupLangFields = (group, index) => ({
  type: UPDATE_GROUP_LANG_FIELDS,
  group,
  index
});

export const createNewGroup = (newGroupData, onSuccess, onError) => ({
  type: ADD_GROUP,
  newGroupData,
  onSuccess,
  onError
});

export const setSearch = (search) => ({
  type: SET_SEARCH_TEXT,
  search
});

export const changePage = (page) => ({
  type: CHANGE_PAGE,
  page
});

export const toggleLimitDropdown = () => ({
  type: TOGGLE_LIMIT_DROPDOWN
});

export const toggleEditModal = (index) => ({
  type: TOGGLE_EDIT_MODAL,
  index
});

export const setLimit = (limit) => ({
  type: SET_LIMIT,
  limit
});

export const sendUpdate = (index, update, onSuccess, onError) => ({
  type: SEND_UPDATE,
  index,
  update,
  onSuccess,
  onError
});

export const setGroupLanguage = (index, languageCode) => ({
  type: SET_GROUP_LANGUAGE,
  index,
  languageCode
});

export const searchUserGroups = (searchTerm, setResultCallback) => ({
  type: SEARCH_USER_GROUPS,
  searchTerm,
  setResultCallback
});

export const setParentGroupSuggestions = (parentGroupSuggestions) => ({
  type: SET_PARENT_GROUP_SUGGESTIONS,
  parentGroupSuggestions
});

export const toggleModal = () => ({
  type: TOGGLE_ADD_GROUP_MODAL
});

export const getAllProductsSuccess = () => ({
  type: GET_ALL_PRODUCTS_SUCCESS
});

export const getAvailableProductsList = (
  parentGroupId,
  groupId,
  gotProducts
) => ({
  type: GET_AVAILABLE_PRODUCTS,
  parentGroupId,
  groupId,
  gotProducts
});

export const fetchProducts = (index) => ({
  type: FETCH_PRODUCTS,
  index
});

export const updateProducts = (index, products) => ({
  type: UPDATE_PRODUCTS,
  index,
  products
});

/*** Reducers ***/
export const DEFAULT_STATE = {
  userGroups: [],
  isEditing: [],
  isEditModalOpen: [],
  groupLanguage: [],
  currentPage: 0,
  totalPages: 1,
  limit: 10,
  limitDropdownOpen: false,
  isModalOpen: false,
  search: {
    text: '',
    fields: [],
    searchAllFields: true
  },
  total: 2,
  countries: [],
  languageOptions: languageFilterOptions,
  parentGroupSuggestions: [],
  isFetchingProducts: false
};

export default function GroupsReducer(
  state = DEFAULT_STATE,
  {
    groups,
    group,
    search,
    total_pages,
    total,
    limit,
    page,
    type,
    index,
    countries,
    languageCode,
    products
  } = {}
) {
  switch (type) {
    case SET_GROUPS: {
      return {
        ...state,
        userGroups: groups.map((item) => {
          return {
            ...item,
            native_name: item.name,
            name: item.name_native_translation ? item.name : null
          };
        }),
        totalPages: total_pages,
        total: total,
        countries: countries,
        isEditing: groups.map(() => false),
        isEditModalOpen: groups.map(() => false),
        groupLanguage: groups.map(() => languageCode),
        parentGroupSuggestions: []
      };
    }
    case SET_SEARCH_TEXT: {
      return {
        ...state,
        search: search
      };
    }
    case SET_LIMIT: {
      return {
        ...state,
        limit: limit,
        currentPage: 0
      };
    }
    case CHANGE_PAGE: {
      return {
        ...state,
        currentPage: page
      };
    }
    case TOGGLE_LIMIT_DROPDOWN: {
      return {
        ...state,
        limitDropdownOpen: !state.limitDropdownOpen
      };
    }

    case TOGGLE_EDIT_MODAL: {
      return {
        ...state,
        isEditModalOpen: state.isEditModalOpen.map((value, i) =>
          i === index ? !state.isEditModalOpen[i] : value
        ),
        isEditing: state.isEditing.map((value, i) =>
          i === index ? false : value
        )
      };
    }

    case UPDATE_GROUP_SUCCESS: {
      return {
        ...state,
        userGroups: state.userGroups.map((item, i) =>
          i === index && group != null ? { ...group } : item
        )
      };
    }

    case UPDATE_GROUP_LANG_FIELDS: {
      return {
        ...state,
        userGroups: state.userGroups.map((item, i) =>
          i === index && group != null ? { ...item, name: group.name } : item
        )
      };
    }

    case SET_GROUP_LANGUAGE: {
      return {
        ...state,
        groupLanguage: state.groupLanguage.map((value, i) =>
          i === index ? languageCode : value
        )
      };
    }

    case TOGGLE_ADD_GROUP_MODAL: {
      return {
        ...state,
        isModalOpen: !state.isModalOpen
      };
    }
    case UPDATE_PRODUCTS: {
      return {
        ...state,
        userGroups: state.userGroups.map((item, i) =>
          i === index ? { ...item, products } : item
        )
      };
    }
    case GET_ALL_PRODUCTS_SUCCESS: {
      return {
        ...state,
        isFetchingProducts: false
      };
    }
    case GET_AVAILABLE_PRODUCTS: {
      return {
        ...state,
        isFetchingProducts: true
      };
    }
    default: {
      return state;
    }
  }
}

const formatGroupData = (groupData, langCode, locale) => {
  let formattedGroupData = {};
  if (!groupData || Object.keys(groupData).length == 0) return null;
  Object.keys(groupData).forEach((field) => {
    if (field !== 'translations') {
      formattedGroupData[field] = groupData[field];
    } else {
      translationFields.forEach((transField) => {
        formattedGroupData[transField] = groupData[field][langCode][transField];
      });
      formattedGroupData['native_name'] = groupData[field][locale]['name'];
      if (!formattedGroupData['native_name']) {
        formattedGroupData['native_name'] =
          groupData[field][languageFilterOptions[0]['code']]['name'];
      }
    }
  });
  return formattedGroupData;
};

/*** Sagas ***/
export function* fetchAllGroupsWorker() {
  try {
    const auth = yield select(getAuth);
    const locale = yield select(getLocale);
    const page = yield select(getCurrentPage);
    const limit = yield select(getLimit);
    const countryList = yield call(getCountriesDropdown, locale, auth);
    const search = yield select(getSearch);

    let data;
    if (search.text.length >= 1) {
      data = yield call(searchGroups, locale, auth, page, limit, search);
    } else {
      data = yield call(getGroups, locale, auth, page, limit);
    }

    if (!search.fields.length && !search.searchAllFields) {
      data = {
        ...data,
        groups: [],
        total: 0,
        total_pages: 0
      };
    }

    yield put(fetchGroupsSuccess(data, countryList.countries, locale));
  } catch (err) {
    yield pushFormattedError(err);
  }
}

export function* fetchGroupWorker(action) {
  try {
    const auth = yield select(getAuth);
    const langCode = yield select(getGroupLanguageCode, action);
    const groupID = (yield select(getUserGroup, action)).id;
    const locale = yield select(getLocale);
    const data = yield call(getGroupById, langCode, auth, groupID);

    yield put(
      updateGroupLangFields(
        formatGroupData(data, langCode, locale),
        action.index
      )
    );

    const productsData = yield call(getProducts, locale, auth, groupID);
    yield put(
      updateProducts(
        action.index,
        productsData.products.map((product) => ({
          name: product.name,
          value: product.key
        }))
      )
    );
  } catch (err) {
    yield pushFormattedError(err);
  }
}

function* updateSubGroups(action, parentGroupId) {
  try {
    const auth = yield select(getAuth);
    const langCode = yield select(getGroupLanguageCode, action);
    const userGroups = yield select(getUserGroups);
    const locale = yield select(getLocale);

    let subGroupIDs = [];
    let groupIndices = [];
    userGroups.forEach(({ id, parent }, i) => {
      if (parent && parentGroupId === parent.id) {
        subGroupIDs.push(id);
        groupIndices.push(i);
      }
    });

    const updatedGroupData = yield all(
      subGroupIDs.map((groupID) => {
        return call(getGroupById, langCode, auth, groupID);
      })
    );

    let subGroupsToUpdate = [];
    for (var i = 0; i < updatedGroupData.length; i++) {
      subGroupsToUpdate.push({
        data: updatedGroupData[i],
        index: groupIndices[i]
      });
    }

    yield all(
      subGroupsToUpdate.map((group) => {
        return put(
          updateGroupSuccess(
            formatGroupData(group.data, langCode, locale),
            group.index
          )
        );
      })
    );
  } catch (err) {
    yield pushFormattedError(err, createFormErrorMessage);
  }
}

export function* updateGroupWorker(action) {
  try {
    const auth = yield select(getAuth);
    const locale = yield select(getLocale);
    const groupLangCode = yield select(getGroupLanguageCode, action);
    const groupID = (yield select(getUserGroup, action)).id;

    const translationReq = yield select(getTranslationRequest, action);

    if (translationReq) {
      yield call(updateGroupTranslation, locale, auth, groupID, translationReq);
    }
    const updateReq = yield select(getUpdateGroupRequest, action);

    var data;
    if (updateReq)
      data = yield call(updateGroup, locale, auth, groupID, updateReq);
    yield put(
      updateGroupSuccess(
        formatGroupData(data, groupLangCode, locale),
        action.index
      )
    );

    if (updateReq || translationReq) {
      yield put(
        pushAttentionNotification({
          title: 'Success',
          messages: 'Changes saved.'
        })
      );

      action.onSuccess();

      yield* updateSubGroups(action, groupID);
    }
  } catch (err) {
    if (err.messages) {
      let messageErrorCategories = Array.from(
        new Set(err.messages.map((error) => error.key))
      );
      action.onError(messageErrorCategories);
    }

    yield pushFormattedError(err, createFormErrorMessage);
  }
}

export function* fetchGroupSearch(action) {
  try {
    const auth = yield select(getAuth);
    const locale = yield select(getLocale);

    if (action.searchTerm) {
      const data = yield call(searchGroups, locale, auth, 0, 100, {
        fields: ['name', 'group_code'],
        text: action.searchTerm
      });
      action.setResultCallback(data.groups);
    }
  } catch (err) {
    yield pushFormattedError(err);
  }
}

export function* createGroupWorker(action) {
  try {
    const auth = yield select(getAuth);
    const locale = yield select(getLocale);

    yield call(createGroup, locale, auth, action.newGroupData);
    action.onSuccess();

    yield put(
      pushAttentionNotification({
        title: 'Success',
        messages: `New group created.`
      })
    );
    yield put(toggleModal());
    yield put(changePage(0));
  } catch (err) {
    if (err.messages) {
      let messageErrorCategories = Array.from(
        new Set(err.messages.map((error) => error.key))
      );
      action.onError(messageErrorCategories);
    }

    yield pushFormattedError(err, createFormErrorMessage);
  }
}

function* getAvailableProductsWorker(action) {
  const auth = yield select(getAuth);
  const locale = yield select(getLocale);
  const productsData = yield call(
    getAvailableProducts,
    locale,
    auth,
    action.parentGroupId,
    action.groupId
  );
  action.gotProducts(productsData.products);
  yield put(getAllProductsSuccess());
}

export function* fetchGroupsSaga() {
  yield all([
    takeEvery(SET_LIMIT, fetchAllGroupsWorker),
    takeEvery(CHANGE_PAGE, fetchAllGroupsWorker),
    takeEvery(ADD_GROUP, createGroupWorker),
    takeEvery(SET_GROUP_LANGUAGE, fetchGroupWorker),
    takeEvery(TOGGLE_EDIT_MODAL, fetchGroupWorker),
    takeEvery(SEND_UPDATE, updateGroupWorker),
    takeEvery(SEARCH_USER_GROUPS, fetchGroupSearch),
    takeEvery(GET_AVAILABLE_PRODUCTS, getAvailableProductsWorker)
  ]);
}



// WEBPACK FOOTER //
// ./src/actions/accounts/groups/groupsActions.js